import { LoanCaseDTO, ProductInstanceDTO } from "@smartx/mikro-vt-client-api"
import { useTranslation } from "react-i18next"
import { IField } from "../../../components/DataList/DataList"
import { productCodes } from "../../../functions/caseHelper"
import { DataListComponent } from "../../../Shared"
import { useProductFieldLabelUtil } from "../../../hooks/useGetProductFieldLabel"
import { VALUE_LIST_ENUM } from "../../../functions/setCodeTableValues"

interface OverviewAmountsAndDurationProps {
  loanCaseData: LoanCaseDTO
}

const OverviewAmountsAndDuration: React.FC<OverviewAmountsAndDurationProps> = ({
  loanCaseData,
}) => {
  const { t } = useTranslation()
  const { productFieldLabelUtil: getLabel } = useProductFieldLabelUtil()

  /* Jóváhagyott hitelösszeg összesen */
  const getProductsTotalAmount = () => {
    let totalLoanAmount = 0

    /* A termékek loanAmount értékének az összege */
    if (loanCaseData.productInstances?.length > 0) {
      loanCaseData.productInstances.forEach((product: ProductInstanceDTO) => {
        /* Ha a termék loanAmount értéke null, akkor nem kell hozzáadni */
        if (product.loanAmount !== null) {
          totalLoanAmount += product.loanAmount
        }
      })
    }

    /* Ha nem történt összegzés, akkor null-t kell visszaadni, hogy a mező ne jelenjen meg a felületen */
    return totalLoanAmount !== 0 && loanCaseData.productInstances?.length > 1
      ? totalLoanAmount
      : null
  }

  /* Igényelt hitelösszeg összesen */
  const getProductsTotalRequestedAmount = () => {
    /* Egylábas ügylet esetén ez a mező nincs töltve, így nem fog megjelenni és nem is kell,
       hogy megjelenjen, kétlábas ügylet esetén töltve van és megjelenik. */
    return loanCaseData.totalRequestedAmount
      ? loanCaseData.totalRequestedAmount
      : null
  }

  const getProductAmountField = (
    product: ProductInstanceDTO,
    field:
      | "requestedAmount"
      | "loanAmount"
      | "requestedAmountInForeignCurrency"
      | "loanAmountInForeignCurrency",
    isEur?: boolean,
  ): IField => {
    return product?.requestedAmount > 0
      ? {
          label: getLabel(product, field),
          value: product[field],
          fieldType: "number",
          appendText: isEur ? "EUR" : null,
        }
      : null
  }

  const getProductMonthField = (product: ProductInstanceDTO): IField => {
    const field = "loanDuration"

    return product?.requestedAmount > 0
      ? {
          label: getLabel(product, field),
          value: product[field],
          appendText: t("common.month"),
          fieldType: "text",
        }
      : null
  }

  const getRequestedDurationField = (loanCase: LoanCaseDTO): IField => {
    const field = "requestedDuration"

    const product = loanCase?.productInstances?.find((el) =>
      [
        productCodes.SZKEH,
        productCodes.BSZKEH,
        productCodes.SZKFH,
        productCodes.ASZKFH,
        productCodes.PALFH,
        productCodes.BIANCO,
      ].includes(el.productCode),
    )

    if (product) {
      return {
        label: getLabel(product, field),
        value: product[field],
        appendText: t("common.month"),
        fieldType: "text",
      }
    }

    return null
  }

  const isEur = (product: ProductInstanceDTO): boolean => {
    return product?.currency === "EUR"
  }

  const renderMultipleProduct = () => {
    /* Folyószámla hitel */
    const FHProduct: ProductInstanceDTO =
      loanCaseData.productInstances.find((product: ProductInstanceDTO) =>
        [
          productCodes.FH,
          productCodes.SZKFH,
          productCodes.ASZKFH,
          productCodes.PALFH,
        ].includes(product.productCode),
      ) || null

    /* Eseti hitel */
    const EHProduct: ProductInstanceDTO =
      loanCaseData.productInstances.find((product: ProductInstanceDTO) =>
        [productCodes.EH, productCodes.SZKEH, productCodes.BSZKEH].includes(
          product.productCode,
        ),
      ) || null

    /* Addóságrendező hitel */
    const exitProduct: ProductInstanceDTO =
      loanCaseData.productInstances.find(
        (product: ProductInstanceDTO) =>
          product.productCode === productCodes.EXIT,
      ) || null

    /* Biankó */
    const biancoProduct: ProductInstanceDTO =
      loanCaseData.productInstances.find(
        (product: ProductInstanceDTO) =>
          product.productCode === productCodes.BIANCO,
      ) || null

    const fieldArr: Array<IField> = [
      biancoProduct
        ? {
            label: getLabel(biancoProduct, "currency"),
            value: biancoProduct.currency,
            fieldType: "CVT",
            list: VALUE_LIST_ENUM.BiancoCurrency,
          }
        : null,

      getProductAmountField(FHProduct, "requestedAmount"),
      getProductAmountField(EHProduct, "requestedAmount"),

      getProductAmountField(
        biancoProduct,
        isEur(biancoProduct)
          ? "requestedAmountInForeignCurrency"
          : "requestedAmount",
        isEur(biancoProduct),
      ),

      {
        label: t("case.fields.productInstance.totalRequestedAmount"),
        value: getProductsTotalRequestedAmount(),
        fieldType: "number",
      },

      getProductAmountField(FHProduct, "loanAmount"),
      getProductAmountField(EHProduct, "loanAmount"),

      getProductAmountField(
        biancoProduct,
        isEur(biancoProduct) ? "loanAmountInForeignCurrency" : "loanAmount",
        isEur(biancoProduct),
      ),

      exitProduct
        ? {
            label:
              t("case.fields.productInstance.PBEXIT") +
              t("case.fields.productInstance.PBEXIT.loanAmount").toLowerCase(),
            value: exitProduct?.loanAmount,
            fieldType: "number",
          }
        : null,

      {
        label: t("case.fields.productInstance.totalAmount"),
        value: getProductsTotalAmount(),
        fieldType: "number",
      },

      exitProduct
        ? {
            label:
              t("case.fields.productInstance.PBEXIT") +
              t("case.fields.productInstance.loanDuration").toLowerCase(),
            value: exitProduct?.loanDuration,
            appendText: t("common.month"),
            fieldType: "text",
          }
        : null,

      getRequestedDurationField(loanCaseData),

      getProductMonthField(FHProduct),
      getProductMonthField(EHProduct),
      getProductMonthField(biancoProduct),
    ]

    return <DataListComponent dataList={fieldArr.filter(Boolean)} />
  }

  return <>{renderMultipleProduct()}</>
}

export default OverviewAmountsAndDuration
